import { useMemo } from 'react';
import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';

import { useGetProjectDetailsQuery } from '../../api/adminProjectsApiSlice';
import {
  handleChangeTrainingPercentage,
  handleChangeTestPercentage,
  handleChangeValidationPercentage,
  setDefaultAllocationModeEdit,
} from '../../state/adminProjectSlice';
import { selectAnnotators, selectDefaultAllocationModeEdit } from '../../state/selector';
import { Annotator, DataSplitSummary } from '../../types/response';

interface UseAllocationConfigurationResults {
  defaultAllocationMode: boolean;
  selectedAnnotators: Annotator[];
  trainingTotal: number;
  testTotal: number;
  validationTotal: number;
  dataSplitSummary: DataSplitSummary;
  changeTrainingPercentage: (value: string, annotatorId: string) => void;
  changeTestPercentage: (value: string, annotatorId: string) => void;
  changeValidationPercentage: (value: string, annotatorId: string) => void;
  inputValues: Record<string, Record<string, string>>;
  setInputValues: Dispatch<SetStateAction<Record<string, Record<string, string>>>>;
  isDataSplitSummaryLoading: boolean;
}

export const useAllocationConfiguration = (): UseAllocationConfigurationResults => {
  // const workflowId = useAppSelector(selectWorkflowId);
  // const {
  //     data: dataSplitSummaryData,
  //     isLoading: isDataSplitSummaryLoading,
  //     isFetching: isDataSplitSummaryFetching,
  // } = useGetDataSplitSummaryQuery({ workflowId: workflowId ?? '' });

  const { projectId } = useParams();
  const {
    data: projectDetailsData,
    isLoading: isProjectDetailsLoading,
    isFetching: isProjectDetailsFetching,
  } = useGetProjectDetailsQuery({ projectId: projectId as string });

  const dataSplitSummary = useMemo(
    () =>
      projectDetailsData?.data?.dataSplitSummary ?? {
        trainingCount: 0,
        testCount: 0,
        validationCount: 0,
      },
    [projectDetailsData]
  );

  const defaultAllocationMode = useAppSelector(selectDefaultAllocationModeEdit);

  const selectedAnnotators = useAppSelector(selectAnnotators);

  const dispatch = useAppDispatch();

  const trainingTotal = useMemo(
    () =>
      selectedAnnotators?.reduce(
        (acc, annotator) => acc + (annotator.allocationPercentage?.trainingPercentage ?? 0),
        0
      ) ?? 0,
    [selectedAnnotators]
  );

  const testTotal = useMemo(
    () =>
      selectedAnnotators?.reduce(
        (acc, annotator) => acc + (annotator.allocationPercentage?.testPercentage ?? 0),
        0
      ) ?? 0,
    [selectedAnnotators]
  );

  const validationTotal = useMemo(
    () =>
      selectedAnnotators?.reduce(
        (acc, annotator) => acc + (annotator.allocationPercentage?.validationPercentage ?? 0),
        0
      ) ?? 0,
    [selectedAnnotators]
  );

  const changeTrainingPercentage = (value: string, annotatorId: string) => {
    if (value === '' || value === '-') {
      return;
    }
    const numValue = Number(value);
    if (!isNaN(numValue)) {
      const clampedValue = Math.max(0, Math.min(100, numValue));
      dispatch(handleChangeTrainingPercentage({ annotatorId, trainingPercentage: clampedValue }));
    }
  };

  const changeTestPercentage = (value: string, annotatorId: string) => {
    const numValue = Number(value);
    if (!isNaN(numValue)) {
      const clampedValue = Math.max(0, Math.min(100, numValue));
      dispatch(handleChangeTestPercentage({ annotatorId, testPercentage: clampedValue }));
    }
  };

  const changeValidationPercentage = (value: string, annotatorId: string) => {
    const numValue = Number(value);
    if (!isNaN(numValue)) {
      const clampedValue = Math.max(0, Math.min(100, numValue));
      dispatch(
        handleChangeValidationPercentage({ annotatorId, validationPercentage: clampedValue })
      );
    }
  };

  const [inputValues, setInputValues] = useState<Record<string, Record<string, string>>>({});

  useEffect(() => {
    const newInputValues: Record<string, Record<string, string>> = {};
    selectedAnnotators?.forEach((annotator) => {
      newInputValues[annotator.id] = {
        trainingPercentage:
          annotator.allocationPercentage?.trainingPercentage !== undefined
            ? String(annotator.allocationPercentage.trainingPercentage)
            : '',
        testPercentage:
          annotator.allocationPercentage?.testPercentage !== undefined
            ? String(annotator.allocationPercentage.testPercentage)
            : '',
        validationPercentage:
          annotator.allocationPercentage?.validationPercentage !== undefined
            ? String(annotator.allocationPercentage.validationPercentage)
            : '',
      };
    });
    setInputValues(newInputValues);
  }, [selectedAnnotators?.map((a) => a.id).join(',')]);
  return {
    defaultAllocationMode: defaultAllocationMode ?? false,
    selectedAnnotators: selectedAnnotators ?? [],
    trainingTotal,
    testTotal,
    validationTotal,
    dataSplitSummary,
    changeTrainingPercentage,
    changeTestPercentage,
    changeValidationPercentage,
    inputValues,
    setInputValues,
    isDataSplitSummaryLoading: isProjectDetailsLoading || isProjectDetailsFetching,
  };
};
